import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faTimes } from '@fortawesome/free-solid-svg-icons';
import { API_CONFIG } from '../config/api';

const NewWorkOrderForm = () => {
    const [formData, setFormData] = useState({
        id_ustanova: '',
        id_aparat: '',
        datum: '',
        vrijeme_pocetka: '',
        vrijeme_zavrsetka: '',
        ukupno_radnih_sati: '',
        relacija: '',
        opis_kvara_intervencija: '',
        opis_poslova: '',
        napomena: '',
        interna_napomena: ''
    });
    const [ustanove, setUstanove] = useState([]);
    const [aparati, setAparati] = useState([]);
    const [allDijelovi, setAllDijelovi] = useState([]);
    // Typeahead inputs
    const [ustanovaInput, setUstanovaInput] = useState('');
    const [showUstanovaOptions, setShowUstanovaOptions] = useState(false);
    const [aparatInput, setAparatInput] = useState('');
    const [showAparatOptions, setShowAparatOptions] = useState(false);
    const [usedParts, setUsedParts] = useState([{ id_dijelovi: '', kolicina: '' }]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const startTimeRef = useRef(null);
    const endTimeRef = useRef(null);
    const [showStartHour, setShowStartHour] = useState(false);
    const [showStartMinute, setShowStartMinute] = useState(false);
    const [showEndHour, setShowEndHour] = useState(false);
    const [showEndMinute, setShowEndMinute] = useState(false);
    const startPickerRef = useRef(null);
    const endPickerRef = useRef(null);
    const [timeWarning, setTimeWarning] = useState('');

    const loggedInUser = JSON.parse(localStorage.getItem('user'));
    const userId = loggedInUser?.id;

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('token');
            if (!token || !userId) {
                setError('Authentication error. Please log in again.');
                setLoading(false);
                return;
            }

            try {
                const [ustanoveRes, aparatiRes, dijeloviRes] = await Promise.all([
                    fetch(API_CONFIG.ENDPOINTS.USTANOVE, { headers: { 'Authorization': `Bearer ${token}` } }),
                    fetch(API_CONFIG.ENDPOINTS.APARATI, { headers: { 'Authorization': `Bearer ${token}` } }),
                    fetch(API_CONFIG.ENDPOINTS.DIJELOVI, { headers: { 'Authorization': `Bearer ${token}` } }),
                ]);

                if (!ustanoveRes.ok || !aparatiRes.ok || !dijeloviRes.ok) {
                    throw new Error('Failed to fetch data for form dropdowns.');
                }

                const ustanoveData = await ustanoveRes.json();
                const aparatiData = await aparatiRes.json();
                const dijeloviData = await dijeloviRes.json();

                setUstanove(ustanoveData);
                setAparati(aparatiData);
                setAllDijelovi(dijeloviData);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [userId]);

    // Effect to calculate total working hours
    useEffect(() => {
        if (formData.vrijeme_pocetka && formData.vrijeme_zavrsetka) {
            const start = new Date(`1970-01-01T${formData.vrijeme_pocetka}`);
            const end = new Date(`1970-01-01T${formData.vrijeme_zavrsetka}`);

            if (start > end) {
                setTimeWarning('Vrijeme početka ne može biti veće od vremena završetka.');
                setFormData(prevData => ({ ...prevData, ukupno_radnih_sati: '' }));
            } else if (end > start) {
                const diff = new Date(end.getTime() - start.getTime());
                const hours = diff.getUTCHours().toString().padStart(2, '0');
                const minutes = diff.getUTCMinutes().toString().padStart(2, '0');
                const seconds = diff.getUTCSeconds().toString().padStart(2, '0');
                setFormData(prevData => ({ ...prevData, ukupno_radnih_sati: `${hours}:${minutes}:${seconds}` }));
                setTimeWarning('');
            } else {
                // Jednaka vremena: nema upozorenja, ali nema ni trajanja
                setFormData(prevData => ({ ...prevData, ukupno_radnih_sati: '' }));
                setTimeWarning('');
            }
        } else {
            setTimeWarning('');
        }
    }, [formData.vrijeme_pocetka, formData.vrijeme_zavrsetka]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        // If changing ustanova, clear selected aparat and aparat search
        if (name === 'id_ustanova') {
            setFormData({ ...formData, id_ustanova: value, id_aparat: '' });
            setAparatInput('');
            return;
        }
        setFormData({ ...formData, [name]: value });
    };

    const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
    const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

    // Close time dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (startPickerRef.current && !startPickerRef.current.contains(event.target)) {
                setShowStartHour(false);
                setShowStartMinute(false);
            }
            if (endPickerRef.current && !endPickerRef.current.contains(event.target)) {
                setShowEndHour(false);
                setShowEndMinute(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getTimeParts = (value) => {
        const [h, m] = (value || '').split(':');
        return { h: h?.padStart(2, '0') || '', m: m?.padStart(2, '0') || '' };
    };

    const setStartHour = (hour) => {
        const { m } = getTimeParts(formData.vrijeme_pocetka);
        const minutes = m || '00';
        setFormData(prev => ({ ...prev, vrijeme_pocetka: `${hour}:${minutes}` }));
        // Keep panel open so user can pick minutes next
    };
    const setStartMinute = (minute) => {
        const { h } = getTimeParts(formData.vrijeme_pocetka);
        const hour = h || '00';
        setFormData(prev => ({ ...prev, vrijeme_pocetka: `${hour}:${minute}` }));
        setShowStartMinute(false);
    };
    const setEndHour = (hour) => {
        const { m } = getTimeParts(formData.vrijeme_zavrsetka);
        const minutes = m || '00';
        setFormData(prev => ({ ...prev, vrijeme_zavrsetka: `${hour}:${minutes}` }));
        // Keep panel open so user can pick minutes next
    };
    const setEndMinute = (minute) => {
        const { h } = getTimeParts(formData.vrijeme_zavrsetka);
        const hour = h || '00';
        setFormData(prev => ({ ...prev, vrijeme_zavrsetka: `${hour}:${minute}` }));
        setShowEndMinute(false);
    };

    const handlePartChange = (index, e) => {
        const { name, value } = e.target;
        const list = [...usedParts];
        list[index][name] = value;
        setUsedParts(list);
    };

    const handleAddPart = () => {
        setUsedParts([...usedParts, { id_dijelovi: '', kolicina: '' }]);
    };

    const handleRemovePart = (index) => {
        const list = [...usedParts];
        list.splice(index, 1);
        setUsedParts(list);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (timeWarning) {
            alert(timeWarning);
            return;
        }
        // Manual validation for required fields
        const requiredFields = [
            'id_ustanova',
            'id_aparat',
            'datum',
            'vrijeme_pocetka',
            'vrijeme_zavrsetka',
            'opis_kvara_intervencija',
            'relacija',
            'opis_poslova'
        ];
        const missing = requiredFields.filter((f) => !String(formData[f] || '').trim());
        if (missing.length > 0) {
            alert('Molimo popunite sva obavezna polja (Ustanova, Aparat, Datum, Vrijeme početka, Vrijeme završetka, Relacija, Opis posla).');
            return;
        }
        const token = localStorage.getItem('token');
        const finalFormData = { ...formData, user_id: userId };

        // Frontend validation: check quantities vs available stock
        const availablePartsMap = new Map(
            allDijelovi
                .filter(d => Number(d.dostupna_kolicina) > 0)
                .map(d => [String(d.id_dijelovi), Number(d.dostupna_kolicina)])
        );
        for (const part of usedParts) {
            if (!part.id_dijelovi || !part.kolicina) continue;
            const available = availablePartsMap.get(String(part.id_dijelovi)) || 0;
            if (Number(part.kolicina) > available) {
                alert(`Nedovoljna količina za odabrani dio. Dostupno: ${available}.`);
                return; // stop submission
            }
        }

        try {
            // Step 1: Create the new work order
            const nalogResponse = await fetch(API_CONFIG.ENDPOINTS.NALOZI, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(finalFormData),
            });

            if (!nalogResponse.ok) {
                throw new Error('Nije uspjelo kreiranje novog radnog naloga.');
            }

            const newNalog = await nalogResponse.json();

            // Step 2: Add used parts
            for (const part of usedParts) {
                if (part.id_dijelovi && part.kolicina) {
                    const partData = {
                        id_nalog: newNalog.id_nalog,
                        id_dijelovi: part.id_dijelovi,
                        kolicina: parseInt(part.kolicina, 10),
                    };

                    const partResponse = await fetch(API_CONFIG.ENDPOINTS.UTROSENI_DIJELOVI, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(partData),
                    });

                    if (!partResponse.ok) {
                        const errorData = await partResponse.json();
                        throw new Error(`Error adding part ${part.id_dijelovi}: ${errorData.error}`);
                    }
                }
            }

            alert('Nalog kreiran uspješno!');
            navigate('/dashboard'); // Redirect to dashboard after creation
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-[50vh] text-gray-600">Učitavanje podataka obrasca...</div>
    );
    if (error) return (
        <div className="max-w-3xl mx-auto p-4">
            <div className="p-3 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">Greška: {error}</div>
        </div>
    );

    // Derived filtered lists for typeahead
    const filteredUstanove = (ustanove || []).filter(u =>
        (u?.naziv_ustanove || '').toLowerCase().includes((ustanovaInput || '').toLowerCase())
    );

    const aparatiForUstanova = (aparati || []).filter(a =>
        String(a.id_ustanova) === String(formData.id_ustanova || '')
    );
    const filteredAparati = aparatiForUstanova.filter(a =>
        (`${a.naziv_aparata} ${a.id_broj}`).toLowerCase().includes((aparatInput || '').toLowerCase())
    );

    const availableParts = (allDijelovi || []).filter(d => Number(d.dostupna_kolicina) > 0);

    return (
        <div className="max-w-3xl mx-auto">
            <div className="mb-5">
                <h2 className="text-3xl font-semibold text-gray-900">Novi radni nalog</h2>
                <p className="text-sm text-gray-600 mt-1">Popunite osnovne podatke, vrijeme rada i eventualno utrošene dijelove.</p>
            </div>
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow border border-gray-200 p-6 md:p-8 space-y-6">
                {/* Serviser - na vrhu, samo prikaz */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Serviser</label>
                    <div className="mt-1 text-gray-900 font-medium">{loggedInUser?.first_name} {loggedInUser?.last_name}</div>
                </div>
                {/* Osnovni podaci */}
                <fieldset className="space-y-5">
                    <legend className="text-lg font-medium text-gray-900">Osnovni podaci</legend>
                    {/* Ustanova Dropdown */}
                    <div>
                    <label className="block text-sm font-medium text-gray-700">Ustanova <span className="text-red-600">*</span></label>
                    <div className="relative mt-1">
                        <div className="flex items-center">
                            <input
                                type="text"
                                value={ustanovaInput}
                                onChange={(e) => { setUstanovaInput(e.target.value); setShowUstanovaOptions(true); }}
                                onFocus={() => setShowUstanovaOptions(true)}
                                placeholder="Počnite kucati za pretragu ustanova"
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pl-3 pr-10 py-3"
                                aria-autocomplete="list"
                            />
                            {formData.id_ustanova && (
                                <button type="button" title="Očisti" onClick={() => { setFormData({ ...formData, id_ustanova: '', id_aparat: '' }); setUstanovaInput(''); setAparatInput(''); }} className="-ml-9 mr-2 text-gray-500 hover:text-gray-700">
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                        {showUstanovaOptions && (
                            <ul
                                className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow"
                                onMouseDown={(e) => e.preventDefault()}
                                onBlur={() => setTimeout(() => setShowUstanovaOptions(false), 100)}
                            >
                                {filteredUstanove.length === 0 && (
                                    <li className="px-3 py-2 text-sm text-gray-500">Nema rezultata</li>
                                )}
                                {filteredUstanove.map(u => (
                                    <li key={u.id_ustanova}>
                                        <button
                                            type="button"
                                            className="w-full text-left px-3 py-2 hover:bg-gray-100"
                                            onClick={() => {
                                                setFormData({ ...formData, id_ustanova: u.id_ustanova, id_aparat: '' });
                                                setUstanovaInput(u.naziv_ustanove);
                                                setAparatInput('');
                                                setShowUstanovaOptions(false);
                                            }}
                                        >
                                            {u.naziv_ustanove}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {/* Hidden select to keep required validation semantics */}
                    <input type="hidden" name="id_ustanova" value={formData.id_ustanova} onChange={handleChange} required />
                </div>

                {/* Aparat Dropdown */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Aparat <span className="text-red-600">*</span></label>
                    <div className="relative mt-1">
                        <div className="flex items-center">
                            <input
                                type="text"
                                value={aparatInput}
                                onChange={(e) => { setAparatInput(e.target.value); setShowAparatOptions(true); }}
                                onFocus={() => setShowAparatOptions(true)}
                                placeholder={!formData.id_ustanova ? "Prvo odaberite ustanovu" : "Pretraži aparate (naziv ili ID)"}
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pl-3 pr-10 py-3 disabled:bg-gray-50 disabled:text-gray-500"
                                disabled={!formData.id_ustanova}
                                aria-autocomplete="list"
                            />
                            {formData.id_aparat && (
                                <button type="button" title="Očisti" onClick={() => { setFormData({ ...formData, id_aparat: '' }); setAparatInput(''); }} className="-ml-9 mr-2 text-gray-500 hover:text-gray-700">
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                        {showAparatOptions && formData.id_ustanova && (
                            <ul
                                className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow"
                                onMouseDown={(e) => e.preventDefault()}
                                onBlur={() => setTimeout(() => setShowAparatOptions(false), 100)}
                            >
                                {filteredAparati.length === 0 && (
                                    <li className="px-3 py-2 text-sm text-gray-500">Nema rezultata</li>
                                )}
                                {filteredAparati.map(a => (
                                    <li key={a.id_aparat}>
                                        <button
                                            type="button"
                                            className="w-full text-left px-3 py-2 hover:bg-gray-100"
                                            onClick={() => {
                                                setFormData({ ...formData, id_aparat: a.id_aparat });
                                                setAparatInput(`${a.naziv_aparata} (${a.id_broj})`);
                                                setShowAparatOptions(false);
                                            }}
                                        >
                                            {a.naziv_aparata} ({a.id_broj})
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {/* Hidden field for validation */}
                    <input type="hidden" name="id_aparat" value={formData.id_aparat} onChange={handleChange} required />
                </div>

                {/* Datum */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Datum <span className="text-red-600">*</span></label>
                    <input type="date" name="datum" value={formData.datum} onChange={handleChange} required className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-3" />
                </div>
                </fieldset>

                {/* Vrijeme rada */}
                <fieldset className="space-y-3">
                    <legend className="text-lg font-medium text-gray-900">Vrijeme rada</legend>
                    <p className="text-sm text-gray-600">Kliknite na polje ili ikonu sata za odabir vremena.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Vrijeme početka <span className="text-red-600">*</span></label>
                        <div ref={startPickerRef} className="relative mt-1">
                            <input
                                ref={startTimeRef}
                                type="text"
                                readOnly
                                name="vrijeme_pocetka"
                                value={formData.vrijeme_pocetka}
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pl-3 pr-10 py-3"
                                placeholder="hh:mm"
                                required
                                onClick={() => { setShowStartHour(true); setShowStartMinute(true); }}
                                onFocus={() => { setShowStartHour(true); setShowStartMinute(true); }}
                            />
                            <button type="button" onClick={() => { setShowStartHour(true); setShowStartMinute(true); }} className="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700">
                                <FontAwesomeIcon icon={faClock} />
                            </button>
                            {showStartHour && (
                                <ul className="absolute z-20 left-0 mt-2 w-24 max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow">
                                    {HOURS.map(h => (
                                        <li key={h}>
                                            <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-gray-100" onClick={() => setStartHour(h)}>{h}</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {showStartMinute && (
                                <ul className="absolute z-20 right-0 mt-2 w-24 max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow">
                                    {MINUTE_OPTIONS.map(m => (
                                        <li key={m}>
                                            <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-gray-100" onClick={() => { setStartMinute(m); setShowStartHour(false); }}>
                                                {m}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Vrijeme završetka <span className="text-red-600">*</span></label>
                        <div ref={endPickerRef} className="relative mt-1">
                            <input
                                ref={endTimeRef}
                                type="text"
                                readOnly
                                name="vrijeme_zavrsetka"
                                value={formData.vrijeme_zavrsetka}
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pl-3 pr-10 py-3"
                                placeholder="hh:mm"
                                required
                                onClick={() => { setShowEndHour(true); setShowEndMinute(true); }}
                                onFocus={() => { setShowEndHour(true); setShowEndMinute(true); }}
                            />
                            <button type="button" onClick={() => { setShowEndHour(true); setShowEndMinute(true); }} className="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700">
                                <FontAwesomeIcon icon={faClock} />
                            </button>
                            {showEndHour && (
                                <ul className="absolute z-20 left-0 mt-2 w-24 max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow">
                                    {HOURS.map(h => (
                                        <li key={h}>
                                            <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-gray-100" onClick={() => setEndHour(h)}>{h}</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {showEndMinute && (
                                <ul className="absolute z-20 right-0 mt-2 w-24 max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow">
                                    {MINUTE_OPTIONS.map(m => (
                                        <li key={m}>
                                            <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-gray-100" onClick={() => { setEndMinute(m); setShowEndHour(false); }}>
                                                {m}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Ukupno radnih sati</label>
                        <input type="text" name="ukupno_radnih_sati" value={formData.ukupno_radnih_sati} readOnly className="mt-1 w-full rounded-md bg-gray-50 border-gray-200 shadow-sm px-3 py-3" />
                    </div>
                </div>
                    {timeWarning && (
                        <div className="text-sm text-red-600">{timeWarning}</div>
                    )}
                </fieldset>

                {/* Opisi */}
                <fieldset className="space-y-5">
                    <legend className="text-lg font-medium text-gray-900">Detalji intervencije</legend>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Relacija <span className="text-red-600">*</span></label>
                            <input type="text" name="relacija" value={formData.relacija} onChange={handleChange} required className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-3" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Opis kvara/intervencije <span className="text-red-600">*</span></label>
                            <textarea name="opis_kvara_intervencija" value={formData.opis_kvara_intervencija} onChange={handleChange} required className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-3" rows="4"></textarea>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Opis poslova <span className="text-red-600">*</span></label>
                            <textarea name="opis_poslova" value={formData.opis_poslova} onChange={handleChange} required className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-3" rows="4"></textarea>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Napomena</label>
                            <textarea name="napomena" value={formData.napomena} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-3" rows="3"></textarea>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Interna napomena</label>
                            <textarea name="interna_napomena" value={formData.interna_napomena} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-3" rows="2"></textarea>
                        </div>
                    </div>
                </fieldset>

                {/* Dynamic Used Parts Section */}
                <fieldset className="space-y-3">
                    <legend className="text-lg font-medium text-gray-900">Utrošeni dijelovi</legend>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-3">
                        {usedParts.map((part, index) => (
                            <div key={index} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                                <select
                                    name="id_dijelovi"
                                    value={part.id_dijelovi}
                                    onChange={(e) => handlePartChange(index, e)}
                                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-3"
                                >
                                    <option value="">-- Odaberite dio --</option>
                                    {availableParts.map(dio => (
                                        <option key={dio.id_dijelovi} value={dio.id_dijelovi}>
                                            {dio.naziv_dijela} (na stanju: {dio.dostupna_kolicina})
                                        </option>
                                    ))}
                                </select>
                                <input
                                    type="number"
                                    name="kolicina"
                                    value={part.kolicina}
                                    onChange={(e) => handlePartChange(index, e)}
                                    placeholder="Količina"
                                    min="1"
                                    className="w-28 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-3"
                                />
                                {/* Quantity validation note */}
                                {(() => {
                                    const selected = availableParts.find(d => String(d.id_dijelovi) === String(part.id_dijelovi));
                                    const available = selected ? Number(selected.dostupna_kolicina) : 0;
                                    const qty = Number(part.kolicina || 0);
                                    const invalid = part.id_dijelovi && qty > 0 && qty > available;
                                    if (!invalid) return null;
                                    return (
                                        <div className="text-sm text-red-600">Nedovoljna količina (dostupno: {available})</div>
                                    );
                                })()}
                                {usedParts.length > 1 && (
                                    <button type="button" onClick={() => handleRemovePart(index)} className="px-3 py-2 rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors">
                                        Ukloni
                                    </button>
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={handleAddPart} className="px-3 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">Dodaj dio</button>
                    </div>
                </fieldset>

                <div className="pt-2 flex items-center justify-end gap-3">
                    <button type="submit" className="px-5 py-3 rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors">Kreiraj radni nalog</button>
                </div>
            </form>
        </div>
    );
};

export default NewWorkOrderForm;